/**
 * Jurisdiction Policies — authoring view
 *
 * Requirement-narrative IA mirroring the tenant `/settings/policies` page:
 * jurisdiction picker drives a 3×2 grid of channel × purpose tiles, each
 * showing the rendered "About the contact" / "About the send" bullets that
 * tenants will see. Cross-cutting rules (channel===null or messagePurpose===null)
 * live in a single pinned panel above the grid.
 *
 * Authors edit a rule's wording inline by clicking a tile bullet, which opens
 * the existing `PolicyRuleForm` modal pre-populated with the rule. Adding a
 * new requirement opens the same form scoped to the selected jurisdiction.
 * The Check Library (separate route) is the home for editing per-check
 * templates.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  JurisdictionPicker,
  RuleFlowCard,
  RuleArrow,
  CollapsibleRuleSection,
  cn,
} from '@respectly/ui';
import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronDown,
  Clock,
  Globe,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  Smartphone,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import type {
  Jurisdiction,
  Rule,
  PRECheckDefinition,
  PREChannel,
  MessagePurpose,
} from '@respectly/shared-types';
import { renderRule, type RenderedRequirement } from '@respectly/utils';
import { usePolicyStore } from '../../stores/usePolicyStore';
import { PolicyRuleForm } from '../../components/policy/PolicyRuleForm';

// ============================================
// Animation
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// ============================================
// Constants
// ============================================

type SupportedChannel = Extract<PREChannel, 'SMS' | 'VOICE' | 'EMAIL'>;

const CHANNELS: { code: SupportedChannel; label: string }[] = [
  { code: 'SMS', label: 'SMS' },
  { code: 'VOICE', label: 'Voice' },
  { code: 'EMAIL', label: 'Email' },
];

const PURPOSES: { code: MessagePurpose; label: string }[] = [
  { code: 'INFORMATIONAL', label: 'Informational' },
  { code: 'MARKETING', label: 'Marketing' },
];

// Channel theming — mirrors the Send Wizard step 2 (ChannelContentCard).
const CHANNEL_CONFIG: Record<
  SupportedChannel,
  {
    icon: React.ElementType;
    accentBg: string;
    accentText: string;
    accentBorder: string;
  }
> = {
  SMS: {
    icon: Smartphone,
    accentBg: 'bg-green-50 dark:bg-green-900/20',
    accentText: 'text-green-700 dark:text-green-400',
    accentBorder: 'border-green-200 dark:border-green-800',
  },
  VOICE: {
    icon: Phone,
    accentBg: 'bg-cyan-50 dark:bg-cyan-900/20',
    accentText: 'text-cyan-700 dark:text-cyan-400',
    accentBorder: 'border-cyan-200 dark:border-cyan-800',
  },
  EMAIL: {
    icon: Mail,
    accentBg: 'bg-amber-50 dark:bg-amber-900/20',
    accentText: 'text-amber-700 dark:text-amber-400',
    accentBorder: 'border-amber-200 dark:border-amber-800',
  },
};

// ============================================
// Component
// ============================================

export function PolicyJurisdictions() {
  const jurisdictions = usePolicyStore((s) => s.jurisdictions);
  const rules = usePolicyStore((s) => s.rules);
  const checkDefinitions = usePolicyStore((s) => s.checkDefinitions);
  const isLoadingJurisdictions = usePolicyStore((s) => s.isLoadingJurisdictions);
  const isLoadingRules = usePolicyStore((s) => s.isLoadingRules);
  const loadJurisdictions = usePolicyStore((s) => s.loadJurisdictions);
  const loadRules = usePolicyStore((s) => s.loadRules);
  const loadCheckDefinitions = usePolicyStore((s) => s.loadCheckDefinitions);

  const [selectedCode, setSelectedCode] = useState<string | null>('US-FED');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [recentCodes, setRecentCodes] = useState<string[]>([]);
  const [visibleChannels, setVisibleChannels] = useState<Set<SupportedChannel>>(
    () => new Set(['SMS', 'VOICE', 'EMAIL']),
  );

  useEffect(() => {
    loadJurisdictions();
    // Fetch the entire ruleset (not just the first 20) so inherited
    // country/state rules are visible alongside GLOBAL ones. There are only a
    // few hundred seeded rules total, so a single large page is cheaper than
    // walking pagination here.
    loadRules(1, 1000);
    loadCheckDefinitions();
  }, [loadJurisdictions, loadRules, loadCheckDefinitions]);

  // Track recently-viewed jurisdictions for the picker.
  useEffect(() => {
    if (!selectedCode) return;
    setRecentCodes((prev) => {
      if (prev[0] === selectedCode) return prev;
      return [selectedCode, ...prev.filter((c) => c !== selectedCode)].slice(0, 5);
    });
  }, [selectedCode]);

  const checkDefMap = useMemo(
    () => new Map<string, PRECheckDefinition>(checkDefinitions.map((d) => [d.checkId, d])),
    [checkDefinitions],
  );

  const selectedJurisdiction = useMemo(
    () => jurisdictions.find((j) => j.code === selectedCode) ?? null,
    [jurisdictions, selectedCode],
  );

  // Effective rules at this jurisdiction = rules at this jurisdiction + every
  // ancestor (so authors see exactly what tenants see).
  const ancestorChain = useMemo(() => {
    if (!selectedJurisdiction) return [] as Jurisdiction[];
    const byCode = new Map(jurisdictions.map((j) => [j.code, j]));
    const chain: Jurisdiction[] = [];
    let current: Jurisdiction | undefined = selectedJurisdiction;
    while (current) {
      chain.push(current);
      current = current.parentCode ? byCode.get(current.parentCode) : undefined;
    }
    const global = byCode.get('GLOBAL');
    if (global && !chain.some((c) => c.code === 'GLOBAL')) chain.push(global);
    return chain;
  }, [jurisdictions, selectedJurisdiction]);

  const ancestorCodes = useMemo(() => new Set(ancestorChain.map((j) => j.code)), [ancestorChain]);

  const effectiveRules = useMemo(
    () => rules.filter((r) => ancestorCodes.has(r.jurisdictionCode) && r.isActive),
    [rules, ancestorCodes],
  );

  const localRulesAtSelected = useMemo(
    () => effectiveRules.filter((r) => r.jurisdictionCode === selectedCode),
    [effectiveRules, selectedCode],
  );

  const inheritedRulesCount = effectiveRules.length - localRulesAtSelected.length;

  const isLoading = isLoadingJurisdictions || isLoadingRules;

  return (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-col h-full"
      >
        {/* Header */}
        <motion.div
          variants={itemVariants}
          className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-center gap-4">
            <Link
              to="/settings/policy"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-100 to-cyan-200 dark:from-cyan-900/40 dark:to-cyan-800/40 flex items-center justify-center">
              <Globe className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Jurisdiction Policies
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Author the requirements tenants see for each channel × purpose, in a
                live preview that mirrors their Policies page.
              </p>
            </div>
            <Link
              to="/settings/policy/checks"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
            >
              <BookOpen className="w-4 h-4" />
              Check Library
            </Link>
          </div>
        </motion.div>

        {/* Picker bar */}
        <motion.div
          variants={itemVariants}
          className="flex-shrink-0 px-6 py-4 flex flex-wrap items-center gap-3 border-b border-slate-200 dark:border-slate-800"
        >
          <div className="min-w-[260px] max-w-md flex-1">
            <JurisdictionPicker
              jurisdictions={jurisdictions}
              value={selectedCode}
              onChange={setSelectedCode}
              recentCodes={recentCodes}
              isLoading={isLoadingJurisdictions}
            />
          </div>
          {selectedJurisdiction && (
            <>
              <Badge variant="outline" className="tabular-nums">
                {localRulesAtSelected.length} local
              </Badge>
              {inheritedRulesCount > 0 && (
                <Badge variant="secondary" className="tabular-nums">
                  {inheritedRulesCount} inherited
                </Badge>
              )}
              {selectedJurisdiction.parentCode && (
                <Badge variant="outline" className="text-[11px]">
                  inherits from {selectedJurisdiction.parentCode}
                </Badge>
              )}
              {/* Channel filter pills — toggle visibility per channel. */}
              <div className="flex items-center gap-1.5">
                {CHANNELS.map((c) => {
                  const cfg = CHANNEL_CONFIG[c.code];
                  const ChIcon = cfg.icon;
                  const active = visibleChannels.has(c.code);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() =>
                        setVisibleChannels((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.code)) next.delete(c.code);
                          else next.add(c.code);
                          if (next.size === 0) return prev;
                          return next;
                        })
                      }
                      aria-pressed={active}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors',
                        active
                          ? cn(cfg.accentBg, cfg.accentText, cfg.accentBorder)
                          : 'bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 hover:text-slate-600 dark:hover:text-slate-300',
                      )}
                    >
                      <ChIcon className="w-3.5 h-3.5" />
                      {c.label}
                    </button>
                  );
                })}
              </div>
              <div className="ml-auto">
                <Button size="sm" onClick={() => setIsAddOpen(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add rule
                </Button>
              </div>
            </>
          )}
        </motion.div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading && rules.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : !selectedJurisdiction ? (
            <p className="text-sm text-slate-400 text-center py-12">
              Pick a jurisdiction to start authoring.
            </p>
          ) : (
            <>
              {/* CSS multi-column layout: each card flows top-to-bottom in a
                  column then wraps, so cards keep their natural height (no
                  shared row height like a CSS grid). Iteration is channel-first
                  so a column groups Info on top of Marketing for the same
                  channel. */}
              <div
                className={cn(
                  'gap-4 [column-fill:_balance]',
                  'columns-1',
                  visibleChannels.size === 1 && 'md:columns-2 max-w-4xl',
                  visibleChannels.size === 2 && 'md:columns-2',
                  visibleChannels.size === 3 && 'md:columns-2 lg:columns-3',
                )}
              >
                {CHANNELS.filter((c) => visibleChannels.has(c.code)).flatMap((channel) =>
                  PURPOSES.map((purpose) => {
                    // Eligibility/constraint rules — fed through renderRule().
                    const cellRules = effectiveRules.filter(
                      (r) =>
                        r.layer !== 'consent_capture' &&
                        (r.channel === channel.code || r.channel === null) &&
                        (r.messagePurpose === purpose.code || r.messagePurpose === null),
                    );
                    // Layer-0 consent_capture rules for this (channel × purpose)
                    // cell. consent_capture rules can target either purpose;
                    // when no rule matches, the engine defaults to ALLOW.
                    const consentCaptureRules = effectiveRules.filter(
                      (r) =>
                        r.layer === 'consent_capture' &&
                        (r.channel === channel.code || r.channel === null) &&
                        (r.messagePurpose === purpose.code || r.messagePurpose === null),
                    );
                    return (
                      <div
                        key={`${channel.code}-${purpose.code}`}
                        className="break-inside-avoid mb-4"
                      >
                        <ChannelPurposeCard
                          channel={channel}
                          purpose={purpose}
                          rules={cellRules}
                          consentCaptureRules={consentCaptureRules}
                          checkDefMap={checkDefMap}
                          selectedCode={selectedCode!}
                          onEdit={(r) => setEditingRule(r)}
                          onAdd={() => setIsAddOpen(true)}
                        />
                      </div>
                    );
                  }),
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Edit existing rule */}
      <PolicyRuleForm
        isOpen={!!editingRule}
        onClose={() => setEditingRule(null)}
        rule={editingRule}
        jurisdictionCode={editingRule?.jurisdictionCode ?? selectedCode ?? 'GLOBAL'}
      />

      {/* Add a new rule scoped to the selected jurisdiction */}
      <PolicyRuleForm
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        rule={null}
        jurisdictionCode={selectedCode ?? 'GLOBAL'}
      />
    </>
  );
}


// ============================================
// ChannelPurposeCard — one card per (channel × purpose) cell. Header is
// "Channel — Purpose" (e.g. "SMS — Informational"). Body has three
// CollapsibleRuleSection groups (Consent / Sending / Opt-out) plus an
// inline Add button for authoring.
// ============================================

interface ChannelPurposeCardProps {
  channel: { code: SupportedChannel; label: string };
  purpose: { code: MessagePurpose; label: string };
  rules: Rule[];
  consentCaptureRules: Rule[];
  checkDefMap: Map<string, PRECheckDefinition>;
  selectedCode: string;
  onEdit: (rule: Rule) => void;
  onAdd: () => void;
}

function ChannelPurposeCard({
  channel,
  purpose,
  rules,
  consentCaptureRules,
  checkDefMap,
  selectedCode,
  onEdit,
  onAdd,
}: ChannelPurposeCardProps) {
  const config = CHANNEL_CONFIG[channel.code];
  const ChannelIcon = config.icon;

  const items = rules.flatMap((rule) =>
    renderRule(rule, checkDefMap).map((bullet) => ({ rule, bullet })),
  );
  const optInItems = items.filter((i) => i.bullet.bucket === 'OPT_IN');
  const sendingItems = items.filter((i) => i.bullet.bucket === 'SENDING');
  const optOutItems = items.filter((i) => i.bullet.bucket === 'OPT_OUT');

  // `totalCount` is the number of rendered cards (one per check bullet + one
  // per consent-capture rule). `uniqueRuleCount` counts distinct rule rows —
  // a rule with multiple checks contributes multiple bullets but is still one
  // rule. We surface both so operators don't miscount when reconciling
  // against the Decision View's `trace` (which lists each rule once).
  const totalCount = items.length + consentCaptureRules.length;
  const uniqueRuleCount =
    new Set(items.map((i) => i.rule.id)).size + consentCaptureRules.length;
  const showsBulletSplit = uniqueRuleCount !== totalCount;

  return (
    <details
      open
      className={cn(
        'group/acc relative rounded-2xl border-2 bg-white dark:bg-slate-900',
        'shadow-sm hover:shadow-md transition-shadow duration-200',
        config.accentBorder,
      )}
    >
      <summary
        className={cn(
          'list-none cursor-pointer select-none',
          'flex items-center justify-between gap-3 px-4 py-3 pr-24',
          'rounded-t-2xl',
          'hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors',
          '[&::-webkit-details-marker]:hidden',
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={cn(
              'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
              config.accentBg,
            )}
          >
            <ChannelIcon className={cn('w-4 h-4', config.accentText)} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100 tracking-wide">
              {channel.label} &mdash; {purpose.label}
            </p>
            <p
              className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums"
              title={
                showsBulletSplit
                  ? 'Each bullet below shows one required check. A single rule contributes one bullet per check, and a rule whose checks span multiple categories (e.g. Consent + Opt-out) appears in each. The Decision View counts unique rules, not bullets.'
                  : undefined
              }
            >
              {showsBulletSplit ? (
                <>
                  {totalCount} requirement{totalCount === 1 ? '' : 's'} (
                  {uniqueRuleCount} rule{uniqueRuleCount === 1 ? '' : 's'})
                </>
              ) : (
                <>
                  {totalCount} {totalCount === 1 ? 'rule' : 'rules'}
                </>
              )}
            </p>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0',
            'group-open/acc:rotate-180',
          )}
        />
      </summary>

      {/* Add button absolutely positioned so the disclosure structure stays
          valid (<summary> must be the first child of <details>). */}
      <div className="absolute top-2 right-9">
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAdd();
          }}
          className="h-7 text-xs"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add
        </Button>
      </div>

      <div className="px-4 pb-4 pt-1">
        <div className="space-y-3">
          <ConsentCaptureFlow
            channel={channel}
            purpose={purpose}
            rules={consentCaptureRules}
            selectedCode={selectedCode}
            onEdit={onEdit}
          />
          <RuleFlow
            pillLabel="Consent Checks"
            pillIcon={Check}
            items={optInItems}
            selectedCode={selectedCode}
            onEdit={onEdit}
          />
          <RuleFlow
            pillLabel="Sending Times"
            pillIcon={Clock}
            items={sendingItems}
            selectedCode={selectedCode}
            onEdit={onEdit}
          />
          <RuleFlow
            pillLabel="Opt-out Respected"
            pillIcon={X}
            items={optOutItems}
            selectedCode={selectedCode}
            onEdit={onEdit}
          />
        </div>
      </div>
    </details>
  );
}

// ============================================
// RuleFlow — vertical stack of authoring rule cards with arrow connectors
// ============================================

function RuleFlow({
  pillLabel,
  pillIcon,
  items,
  selectedCode,
  onEdit,
}: {
  pillLabel: string;
  pillIcon: LucideIcon;
  items: { rule: Rule; bullet: RenderedRequirement }[];
  selectedCode: string;
  onEdit: (rule: Rule) => void;
}) {
  if (items.length === 0) return null;
  return (
    <CollapsibleRuleSection
      label={pillLabel}
      count={items.length}
      icon={pillIcon}
    >
      <div>
        {items.map(({ rule, bullet }, i) => {
          const isInherited = rule.jurisdictionCode !== selectedCode;
          const primaryCheckId = rule.requiredChecks?.[0]?.checkId ?? null;
          const detail =
            rule.description && rule.description.trim() !== bullet.text.trim()
              ? rule.description
              : null;
          return (
            <React.Fragment key={`${rule.id}-${i}`}>
              {i > 0 && <RuleArrow />}
              <RuleFlowCard
                checkId={primaryCheckId}
                bucket={bullet.bucket}
                name={rule.name}
                title={bullet.text}
                citation={bullet.citation}
                detail={detail}
                effect={rule.effect}
                inheritedFrom={isInherited ? rule.jurisdictionCode : null}
                subPillLabel={!isInherited ? rule.jurisdictionCode : null}
                actions={
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(rule);
                    }}
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"
                    title={isInherited ? `Edit inherited rule (managed at ${rule.jurisdictionCode})` : 'Edit rule'}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                }
              />
            </React.Fragment>
          );
        })}
      </div>
    </CollapsibleRuleSection>
  );
}

// ============================================
// ConsentCaptureFlow — "Opt-in Requirements" bucket. Renders the layer-0
// consent_capture rules that decide whether INFORMATIONAL consent is
// auto-granted at contact creation. Marketing rows show a static notice.
// ============================================

function ConsentCaptureFlow({
  channel,
  purpose,
  rules,
  selectedCode,
  onEdit,
}: {
  channel: { code: SupportedChannel; label: string };
  purpose: { code: MessagePurpose; label: string };
  rules: Rule[];
  selectedCode: string;
  onEdit: (rule: Rule) => void;
}) {
  // Resolve the most-specific consent_capture rule for this channel.
  const channelPinned = rules.filter((r) => r.channel === channel.code);
  const channelNull = rules.filter((r) => r.channel === null);
  const orderedRules = [...channelPinned, ...channelNull];

  return (
    <CollapsibleRuleSection
      label="Opt-in Requirements"
      count={orderedRules.length}
      icon={UserPlus}
    >
      {orderedRules.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-900/10 p-3 text-[13px] text-emerald-900 dark:text-emerald-200 leading-relaxed">
          <strong>Auto-grant allowed by default.</strong>{' '}
          {channel.label} {purpose.label.toLowerCase()} consent will be
          recorded automatically when a contact is created, until a
          jurisdiction rule says otherwise.
        </div>
      ) : (
        <div>
          {orderedRules.map((rule, i) => {
            const isInherited = rule.jurisdictionCode !== selectedCode;
            return (
              <React.Fragment key={rule.id}>
                {i > 0 && <RuleArrow />}
                <RuleFlowCard
                  checkId={null}
                  bucket="OPT_IN"
                  name={rule.name}
                  title={rule.description || rule.name}
                  citation={rule.sourceRef ?? null}
                  detail={null}
                  effect={rule.effect}
                  inheritedFrom={isInherited ? rule.jurisdictionCode : null}
                  subPillLabel={!isInherited ? rule.jurisdictionCode : null}
                  actions={
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(rule);
                      }}
                      className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"
                      title={
                        isInherited
                          ? `Edit inherited rule (managed at ${rule.jurisdictionCode})`
                          : 'Edit rule'
                      }
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  }
                />
              </React.Fragment>
            );
          })}
        </div>
      )}
    </CollapsibleRuleSection>
  );
}
